import { useEffect, useRef } from 'react';

import { VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import ErrorButton from 'components/buttons/ErrorButton';
import PrimaryButton from 'components/buttons/PrimaryButton';
import FormErrorText from 'components/text/FormErrorText';

import useAutosizeInputFlexBasis from './hooks/useAutosizeFieldWithButton';
import useTranslation from './hooks/useTranslation';

import { ENTER_KEY, TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const OnboardingFormContainer: FunctionComponent<Props> = ({
    children,
    className = '',
    errors,
    onNext,
    testId = TEST_ID
}) => {
    const innerWrapperRef = useRef<HTMLDivElement>(null);
    const inputContainerRef = useRef<HTMLDivElement>(null);

    useAutosizeInputFlexBasis({ flexRowRef: innerWrapperRef, inputContainerRef });
    const { ENTER, PLEASE_CORRECT_THE_FOLLOWING } = useTranslation();

    const hasErrors = errors && Object.keys(errors).length > 0;

    const handleClick = () => {
        if (hasErrors) {
            return;
        }

        onNext?.();
    };

    useEffect(() => {
        const currentInnerWrapperRef = innerWrapperRef.current;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === ENTER_KEY) {
                event.preventDefault();
                onNext?.();
            }
        };

        currentInnerWrapperRef?.addEventListener('keydown', handleKeyDown);

        return () => {
            currentInnerWrapperRef?.removeEventListener('keydown', handleKeyDown);
        };
    }, [onNext]);

    return (
        <Wrapper className={`${className} ${styles.wrapper}`} testId={testId}>
            <div className={styles.childrenWrapper} ref={innerWrapperRef}>
                <div className={styles.inputContainer} ref={inputContainerRef}>
                    {children}
                </div>

                <div className={styles.buttonContainer}>
                    {hasErrors ? (
                        <ErrorButton className={styles.errorButton} text={ENTER} />
                    ) : (
                        <PrimaryButton
                            className={styles.primaryButton}
                            onClick={handleClick}
                            text={ENTER}
                        />
                    )}
                </div>
            </div>

            {hasErrors && (
                <VerticalContainer>
                    <FormErrorText text={PLEASE_CORRECT_THE_FOLLOWING} />

                    {Object.values(errors).map((error, index) => (
                        <FormErrorText key={index} text={error.message} />
                    ))}
                </VerticalContainer>
            )}
        </Wrapper>
    );
};

export default OnboardingFormContainer;
